import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box, Typography } from "@mui/material";
import EasyAnchorLink from "../../UI/AnchorLink";

import { GridColDef } from "@mui/x-data-grid-premium";
import { renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import KuebikoQueryCell from "../UI/KuebikoQueryCell";

import "../BrandMetadataReview.css";

interface PlacesOmniReviewProps {
  omni_key: string;
  country_code: string;
}

const ReviewPlacesOmniCountryCount: React.FC<PlacesOmniReviewProps> = ({
  omni_key,
  country_code,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();

  const columns_def: GridColDef[] = [
    {
      field: "chain_id",
      headerName: "Chain ID",
      type: "string",
      width: 150,
    },
    {
      field: "external_id",
      headerName: "External ID",
      type: "string",
      width: 150,
    },
    {
      field: "omni_key",
      headerName: "Omni Key",
      type: "string",
      width: 150,
    },
    {
      field: "metadata_country_code",
      headerName: "Metadata Country Code",
      description: "The country code as found in the brand's metadata.",
      type: "string",
      width: 200,
    },
    {
      field: "actual_country_code",
      headerName: "Actual Country Code",
      description: "The country code as found in the places table.",
      type: "string",
      width: 200,
    },
    {
      field: "metadata_country_count",
      headerName: "Metadata Country Count",
      description:
        "The count of places for the country as per the brand's metadata.",
      type: "number",
      width: 200,
      renderCell: renderNumberCell,
    },
    {
      field: "actual_country_code_count",
      headerName: "Actual Country Count",
      description:
        "The count of places for the country as per the places table.",
      type: "number",
      width: 200,
      renderCell: renderNumberCell,
    },
    {
      field: "kuebiko_query",
      headerName: "Kuebiko Query",
      description:
        "Copy or view the Kuebiko query used to review places for retraction.",
      filterable: false,
      sortable: false,
      width: 250,
      renderCell: (params) => {
        const queryTemplate = `
        SELECT id, name, country_code, business_category_id, address, chain_id, website,
            external_ids -> 'EXTERNAL_ID' AS "omni_id"
        FROM "places"
        WHERE external_ids -> 'EXTERNAL_ID' IS NOT NULL
        AND country_code = 'COUNTRY_CODE'
        `;

        const columnMappings = {
          EXTERNAL_ID: params.row.external_id,
          COUNTRY_CODE: params.row.actual_country_code,
        };

        return (
          <KuebikoQueryCell
            queryTemplate={queryTemplate}
            columnMappings={columnMappings}
            tooltip="Kuebiko Query"
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (omni_key && country_code) {
      setLoading(true);
      const params = new URLSearchParams({
        omni_key: omni_key,
        country_code: country_code,
      });

      const fetchCountryCounts = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_metadata/places_brand_country_counts?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch places country counts to review for omni ${omni_key} + ${country_code}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchCountryCounts();
    } else {
      setData([]);
    }
  }, [omni_key, country_code]);

  return (
    <Box className="metadata-review-container">
      <Box display="flex" alignItems="center" id="country-counts">
        <EasyAnchorLink id="country-counts" />
        <Typography variant="h6" gutterBottom sx={{ fontSize: "1.25rem" }}>
          Country Code Counts
        </Typography>
      </Box>

      <GaneshDataGrid
        gridId="placesReviewOmniCountryCounts"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data.length > 0 ? true : false}
        customHeight={data.length > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewPlacesOmniCountryCount;
